/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useContext, useRef, useEffect, ReactNode } from "react";
import { makeStyles } from "@material-ui/core";
import { StandardTextFieldProps } from "@material-ui/core/TextField/TextField";
import { tesaGrey4, tesaGrey6, tesaRed, tesaGrey1, tesaBlue } from "../../styles/colors";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import CustomButtonText from "../Button/CustomButtonText";

export interface CustomTextFieldDynamicProps extends StandardTextFieldProps {
  editPermission?: boolean;
  defaultFocused?: boolean;
  dirtyFlag?: boolean;
  header?: ReactNode;
  hide?: boolean;
  keepFocusAfterEdit?: boolean;
  onDelete?: any;
  onCancel?: any;
  onSave?: any;
  saveOnBlur?: boolean;
}

const useStyles = makeStyles((theme) => {
  return {
    root: {
      fontSize: "1rem",
    },
    bottomContainer: {
      textAlign: "right",
      marginBottom: theme.spacing(2.5),
      marginLeft: theme.spacing(2),
    },
    customButtonText: {
      marginLeft: theme.spacing(3),
    },
    dtfDefault: { padding: "0.5rem" },
    dtfFocused: {
      border: `1px solid ${tesaGrey4}`,
      fontFamily: "Proxima Nova",
      outline: "none",
      padding: "1rem",
      resize: "none",
      width: "100%",
    },
    header: {
      paddingLeft: "0.25rem",
      paddingTop: "0.25rem",
    },
    headerFocused: {
      marginBottom: "0.5rem",
    },
    helperText: {
      color: tesaGrey1,
      float: "left",
    },
    p: {
      marginBottom: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      minHeight: "2rem",
      padding: "0.25rem",
      "&:hover": {
        backgroundColor: tesaGrey6,
      },
    },
  };
});

const CustomTextFieldDynamic: React.FC<CustomTextFieldDynamicProps> = (props) => {
  const classes = useStyles(props);
  const {
    defaultFocused,
    dirtyFlag,
    editPermission,
    header,
    helperText,
    hide,
    keepFocusAfterEdit,
    onBlur,
    onCancel,
    onChange,
    onDelete,
    onSave,
    saveOnBlur,
    value,
  } = props;
  const dtfRef = useRef<HTMLTextAreaElement>(null);
  const { showSnackbar } = useContext(ErrorSnackbarContext);

  const [focused, setFocused] = useState(defaultFocused);

  useEffect(() => {
    if (focused) {
      dtfRef.current?.focus();
    }
  }, [focused, hide]);

  useEffect(() => {
    if (!hide && defaultFocused) {
      setFocused(true);
    }
  }, [hide]);

  if (hide) {
    return <></>;
  }
  if (focused) {
    return (
      <>
        <div className={[classes.header, focused ? classes.headerFocused : ""].join(" ")}>{header}</div>
        <textarea
          className={[classes.root, classes.dtfFocused].join(" ")}
          onBlur={(e) => {
            if (saveOnBlur && dirtyFlag) {
              onSave();
            }
            if (onBlur) {
              onBlur(e);
            }
            if (!keepFocusAfterEdit) {
              setFocused(false);
            }
          }}
          onChange={onChange}
          ref={dtfRef}
          rows={10}
          value={value as string}
        />
        <span className={classes.helperText}>{helperText}</span>
        <div className={classes.bottomContainer}>
          {onDelete && (
            <CustomButtonText
              className={classes.customButtonText}
              onMouseDown={() => {
                onDelete();
                setFocused(false);
              }}
              label="Delete"
              color={tesaRed}
            />
          )}
          {onCancel && (
            <CustomButtonText
              className={classes.customButtonText}
              onMouseDown={() => {
                onCancel();
                setFocused(false);
              }}
              label="Cancel"
              color={tesaGrey1}
            />
          )}
          {onSave && (
            <CustomButtonText
              className={classes.customButtonText}
              onMouseDown={() => {
                onSave();
                setFocused(false);
              }}
              label="Save"
              color={tesaBlue}
            />
          )}
        </div>
      </>
    );
  }
  return (
    <div className={[classes.root, classes.dtfDefault].join(" ")}>
      <div className={classes.header}>{header}</div>
      <p
        className={classes.p}
        onClick={() => {
          if (editPermission) {
            setFocused(true);
          } else {
            showSnackbar("You do not have edit access for this text", "error");
          }
        }}
      >
        {value as string}
      </p>
    </div>
  );
};

export default CustomTextFieldDynamic;
